// DIRECTIVE #023 Change 2: universal QB default with per-variety memory
// override. Today's defaults: Specialty + Fillers/Greens land on EB
// (smallest), which is too small for power-buyer cart-fill at the cost
// of an extra explicit click. Universal default flips to QB; clients who
// previously picked EB for a variety keep that preference via the
// per-variety pref store (preferences.ts).
//
// This module is the single source of truth for the resolved box size
// across all entry points (catalog detail page toggle, QuickAddPopover
// pill, MobileBottomSheet pill, PowerBuyerRow silent default). All four
// call sites read getDefaultBoxUnit(variety, savedPref) and pass the
// result through to addToBox via CartAddInput.boxCode.

import type { BoxCapacity, BoxCode, Variety } from "@/lib/catalog/types";
import {
  boxCapacityForLength,
  effectiveDefaultLength,
  smallestAvailableBox,
} from "./pricing";
import { readVarietyPref } from "./preferences";

const BOX_ORDER_PREFER_QB: BoxCode[] = ["QB", "EB", "HB"];

// Pure resolver: given a variety + an optional saved choice (from
// localStorage via readVarietyPref().boxCode), return the box code the
// UI should default to. Universal rule = QB first, then whatever the
// variety supports. Saved choice wins.
export function getDefaultBoxUnit(
  variety: Variety,
  savedBoxCode?: BoxCode | null,
): BoxCode | null {
  const len = effectiveDefaultLength(variety);
  const cap: BoxCapacity | null = boxCapacityForLength(variety, len);
  if (!cap) {
    if (
      typeof process !== "undefined" &&
      process.env?.DEBUG_V3 &&
      typeof console !== "undefined"
    ) {
      console.log("[V3] getDefaultBoxUnit(no cap):", variety.slug);
    }
    return null;
  }

  let resolved: BoxCode | null = null;
  if (savedBoxCode && cap[savedBoxCode] !== null) {
    resolved = savedBoxCode;
  } else {
    for (const code of BOX_ORDER_PREFER_QB) {
      if (cap[code] !== null) {
        resolved = code;
        break;
      }
    }
    if (!resolved) resolved = smallestAvailableBox(cap);
  }

  // DIRECTIVE #024 Step 1: sanity-log the resolved size for hydrangea so
  // the V3 packer downstream can be cross-checked against this value
  // when bugs reproduce. Gated by DEBUG_V3 to avoid prod console noise.
  if (
    typeof process !== "undefined" &&
    process.env?.DEBUG_V3 &&
    typeof console !== "undefined" &&
    variety.purchaseUnit === "box"
  ) {
    console.log(
      `[V3] getDefaultBoxUnit(${variety.slug})`,
      { saved: savedBoxCode ?? null, resolved },
    );
  }

  return resolved;
}

// Convenience: read the saved boxCode from per-variety prefs and resolve
// the default in one call. Returns null when no box size is available
// for the variety at all.
export function resolveDefaultBoxUnit(variety: Variety): BoxCode | null {
  if (typeof window === "undefined") {
    return getDefaultBoxUnit(variety);
  }
  const saved = readVarietyPref(variety.slug);
  return getDefaultBoxUnit(variety, saved?.boxCode ?? null);
}
